import React, { useState, useRef, useEffect, useMemo } from 'react';
import './SetFilter.css';

interface SetOption {
  code: string;
  name: string;
  count: number;
}

interface SetFilterProps {
  sets: SetOption[];
  selectedSet: string | null;  // null = "All Sets"
  onChange?: (setCode: string | null) => void;
}

const SetFilter: React.FC<SetFilterProps> = ({ sets, selectedSet, onChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Filter sets by search term
  const filteredSets = useMemo(() => {
    if (!searchTerm) return sets;

    const lower = searchTerm.toLowerCase();
    return sets.filter(set =>
      set.name.toLowerCase().includes(lower) ||
      set.code.toLowerCase().includes(lower)
    );
  }, [sets, searchTerm]);

  // Get display text for the trigger button
  const selectedSetName = useMemo(() => {
    if (selectedSet === null) return 'All Sets';
    const found = sets.find(s => s.code === selectedSet);
    return found ? found.name : 'All Sets';
  }, [selectedSet, sets]);

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  // Focus search input when dropdown opens
  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isOpen]);

  // Handle keyboard events
  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Escape') {
      setIsOpen(false);
      setSearchTerm('');
    }
  };

  const handleSelect = (setCode: string | null) => {
    onChange?.(setCode);
    setIsOpen(false);
    setSearchTerm('');
  };

  const toggleDropdown = () => {
    setIsOpen(!isOpen);
    if (isOpen) {
      setSearchTerm('');
    }
  };

  return (
    <div className="set-filter" ref={dropdownRef} onKeyDown={handleKeyDown}>
      <label>Set</label>
      <button
        className={`set-filter-trigger ${isOpen ? 'open' : ''}`}
        onClick={toggleDropdown}
        type="button"
      >
        <span className="set-filter-trigger-text">{selectedSetName}</span>
        <svg
          className={`set-filter-chevron ${isOpen ? 'rotated' : ''}`}
          width="12"
          height="12"
          viewBox="0 0 12 12"
          fill="none"
        >
          <path
            d="M3 4.5L6 7.5L9 4.5"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>

      {isOpen && (
        <div className="set-filter-dropdown">
          <div className="set-filter-search-wrapper">
            <input
              ref={searchInputRef}
              type="text"
              className="set-filter-search"
              placeholder="Search sets..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <div className="set-filter-options">
            <button
              className={`set-filter-option ${selectedSet === null ? 'selected' : ''}`}
              onClick={() => handleSelect(null)}
              type="button"
            >
              All Sets
            </button>
            {filteredSets.map(set => (
              <button
                key={set.code}
                className={`set-filter-option ${selectedSet === set.code ? 'selected' : ''}`}
                onClick={() => handleSelect(set.code)}
                type="button"
              >
                {set.name} ({set.code.toUpperCase()})
              </button>
            ))}
            {filteredSets.length === 0 && searchTerm && (
              <div className="set-filter-no-results">No sets found</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default SetFilter;
